import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Edit, Trash2, DollarSign, School, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { schoolMealCostAPI, lookupDataAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const mealCostSchema = z.object({
  schoolId: z.string().min(1, 'School is required'),
  className: z.string().min(1, 'Class is required'),
  costPerStudent: z.number().min(0, 'Cost must be positive'),
});

const SchoolMealCostManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [costs, setCosts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState('');

  const classes = ['ECD', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(mealCostSchema),
    defaultValues: {
      schoolId: '',
      className: '',
      costPerStudent: 0,
    },
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      loadCosts(selectedSchool);
    }
  }, [selectedSchool]);

  const loadInitialData = async () => {
    try {
      const schoolsData = await lookupDataAPI.getSchools(user?.branch_id);
      setSchools(schoolsData.schools || []);
    } catch (error) {
      console.error('Failed to load schools:', error);
      toast.error('Failed to load schools');
    }
  };

  const loadCosts = async (schoolId) => {
    try {
      const costsData = await schoolMealCostAPI.getMealCosts(schoolId);
      setCosts(costsData || []);
    } catch (error) {
      console.error('Failed to load costs:', error);
      setCosts([]);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const costData = {
        school_id: data.schoolId,
        class_name: data.className,
        cost_per_student: data.costPerStudent,
        is_active: true,
      };

      if (editingCost) {
        await schoolMealCostAPI.updateMealCost(editingCost.id, costData);
        toast.success('Meal cost updated successfully!');
      } else {
        await schoolMealCostAPI.createMealCost(costData);
        toast.success('Meal cost created successfully!');
      }
      
      setShowForm(false);
      setEditingCost(null);
      reset();
      if (selectedSchool) {
        loadCosts(selectedSchool);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save meal cost';
      toast.error('Failed to save meal cost', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    reset({
      schoolId: cost.school_id,
      className: cost.class_name,
      costPerStudent: cost.cost_per_student,
    });
    setShowForm(true);
  };

  const handleDelete = async (cost) => {
    if (window.confirm('Are you sure you want to delete this meal cost?')) {
      try {
        await schoolMealCostAPI.updateMealCost(cost.id, { is_active: false });
        toast.success('Meal cost deactivated successfully!');
        if (selectedSchool) {
          loadCosts(selectedSchool);
        }
      } catch (error) {
        toast.error('Failed to delete meal cost');
      }
    }
  };

  const handleAddNew = () => {
    setEditingCost(null);
    reset({
      schoolId: selectedSchool || '',
      className: '',
      costPerStudent: 0,
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">School Meal Cost Management</h1>
            <p className="text-gray-600 mt-2">Manage meal costs per class for schools</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add Cost
            </Button>
          </div>
        </div>
      </motion.div>

      {/* School Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <Label htmlFor="schoolSelect">Select School:</Label>
            <Select
              id="schoolSelect"
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="w-64"
            >
              <option value="">Choose a school...</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>{editingCost ? 'Edit Meal Cost' : 'Add Meal Cost'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="schoolId">School</Label>
                  <Controller
                    name="schoolId"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} error={errors.schoolId?.message}>
                        <option value="">Select School</option>
                        {schools.map(school => (
                          <option key={school.id} value={school.id}>
                            {school.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="className">Class</Label>
                  <Controller
                    name="className"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} error={errors.className?.message}>
                        <option value="">Select Class</option>
                        {classes.map(cls => (
                          <option key={cls} value={cls}>
                            Class {cls}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="costPerStudent">Cost per Student (NPR)</Label>
                  <Controller
                    name="costPerStudent"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        error={errors.costPerStudent?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" loading={loading}>
                    {editingCost ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingCost(null);
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Costs List */}
      {selectedSchool && (
        <Card>
          <CardHeader>
            <CardTitle>Meal Costs for {schools.find(s => s.id === selectedSchool)?.name}</CardTitle>
          </CardHeader>
          <CardContent>
            {costs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No meal costs configured for this school.</p>
            ) : (
              <div className="space-y-3">
                {costs.map((cost) => (
                  <div key={cost.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <p className="font-medium">Class {cost.class_name}</p>
                        <p className="text-sm text-gray-600">NPR {cost.cost_per_student} per student</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={cost.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {cost.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(cost)}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(cost)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SchoolMealCostManagement;